import { useRef, useState } from "react"
import { Box, Popover, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { Allergen } from "../../../../../admin/products/productsTypes"

import { PlannerTag, PlannerTagVariant } from "../PlannerTag"
import { AllergenOptionChips } from "./AllergenOptionChips"
import { cellFieldMissingStyle } from "./cellFieldStyles"

const useStyles = makeStyles()((theme) => ({
    chips: {
        // Chip height plus the padding, so an empty row keeps the height of a filled one.
        minHeight: "1.75rem",
        padding: ".25rem",
        borderRadius: ".25rem",
    },
    chipsError: cellFieldMissingStyle(theme),
    popover: {
        padding: ".75rem",
        maxWidth: "16rem",
    },
}))

type Props = Readonly<{
    value: ReadonlyArray<Allergen>
    hasError: boolean
    isReadOnly: boolean
    isOptional?: boolean
    onChange: (value: ReadonlyArray<Allergen>) => void
}>

export function CellAllergensField({ value, hasError, isReadOnly, isOptional = false, onChange }: Props) {
    const { classes, cx } = useStyles()

    const anchorRef = useRef<HTMLDivElement | null>(null)
    const [isPickerOpen, setPickerOpen] = useState(false)

    const toggleAllergen = (allergen: Allergen) =>
        onChange(
            value.some((selected) => selected.id === allergen.id)
                ? value.filter((selected) => selected.id !== allergen.id)
                : [...value, allergen],
        )

    return (
        <>
            <Stack
                ref={anchorRef}
                direction="row"
                flexWrap="wrap"
                alignItems={hasError ? "flex-start" : "center"}
                columnGap=".25rem"
                // The dashed alert box gives its chips more room to breathe than the plain row does.
                rowGap={hasError ? ".625rem" : ".25rem"}
                className={cx(classes.chips, hasError && classes.chipsError)}
            >
                {value.map((allergen) => (
                    <PlannerTag
                        key={allergen.id}
                        variant={PlannerTagVariant.ALLERGEN}
                        onRemove={isReadOnly ? undefined : () => toggleAllergen(allergen)}
                    >
                        {allergen.name}
                    </PlannerTag>
                ))}
                {!isReadOnly && (
                    <PlannerTag variant={PlannerTagVariant.ADD} onClick={() => setPickerOpen(true)}>
                        <LocalizedStrict
                            id={
                                isOptional
                                    ? "merchant-week-menu-cell-add-allergen-label-optional"
                                    : "merchant-week-menu-cell-add-allergen-label"
                            }
                        >
                            <span>Allergen</span>
                        </LocalizedStrict>
                    </PlannerTag>
                )}
            </Stack>
            <Popover
                open={isPickerOpen}
                anchorEl={anchorRef.current}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                onClose={() => setPickerOpen(false)}
            >
                <Box className={classes.popover}>
                    <AllergenOptionChips selected={value} onToggle={toggleAllergen} />
                </Box>
            </Popover>
        </>
    )
}
